import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Download,
  User,
  Car,
  Building2,
  Shield,
  Loader2,
  FileCheck,
  ChevronRight,
  Wrench,
  AlertTriangle,
  Send,
  Mail,
  Phone,
  MapPin,
  Plus,
  CreditCard,
  Factory,
  Paintbrush,
  Calendar,
  Gauge,
  Clock,
  Euro,
  ArrowLeftRight,
  Upload,
  Fuel,
  FileText,
  Ban,
  MoreVertical,
  XCircle,
  Gift,
  Edit2,
  CheckCircle2,
  RefreshCw,
  Siren,
  Banknote,
} from 'lucide-react';
import { assistanceSentence } from '@/features/assistance/assistanceWording';
import { toast } from 'sonner';
import { useContract } from '@/features/contracts/hooks/useContract';
import { useContractPdfBlob } from '@/features/contracts/hooks/useContractPdfBlob';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useWarrantyDetails } from '@/features/warranties/hooks/useWarrantyDetails';
import { WARRANTY_TYPE_LABELS, VEHICLE_TYPE_LABELS, DURATION_LABELS } from '@/features/warranties/types/warranty.types';
import { useSociety } from '@/features/societies/hooks/useSocieties';
import { useVendorWording } from '@/hooks/useVendorWording';
import { PaymentDetailsCard } from '@/features/contracts/components/PaymentDetailsCard';
import { PdfPreviewCard } from '@/Components/shared/PdfPreviewCard';
import { DetailHeader } from '@/Components/shared/DetailHeader';
import { InfoCard, InfoRow } from '@/Components/shared/InfoCard';
import { ContractRefundsCard } from '@/features/contracts/components/ContractRefundsCard';
import { ProgressTimeline } from '@/Components/shared/ProgressTimeline';
import { StatusBadge, contractStatusToVariant, contractStatusLabel, deliveryProofStateToVariant, deliveryProofStateLabel } from '@/Components/shared/StatusBadge';
import { EmptyState } from '@/Components/shared/EmptyState';
import {
  formatPrice,
  formatFullName,
  formatAddress,
  formatPhone,
  formatDuration,
  getContractDisplayPrice,
} from '@/features/contracts/utils/contractFormatter';
import { formatDate, formatRelativeDate } from '@/utils/dateUtils';
import { formatMileage } from '@/utils/formatters';
import { contractApi } from '@/features/contracts/api/contractApi';
import { DirectPaymentModal } from '@/features/contracts/components/DirectPaymentModal';
import { SendNotificationConfirmDialog } from '@/features/contracts/components/SendNotificationConfirmDialog';
import { Can } from '@/Components/auth/Can';
import { GenericAuditLogsCard } from '@/Components/shared/GenericAuditLogsCard';
import { getSavStatusLabel, getSavStatusColorClass } from '@/utils/savStatusHelpers';
import { deliveryProofApi } from '@/features/delivery-proof/api/deliveryProofApi';
import { CancelContractModal } from '@/features/contracts/components/CancelContractModal';
import { RefundContractModal } from '@/features/contracts/components/RefundContractModal';
import { ReactivateStripeSubscriptionModal } from '@/features/contracts/components/ReactivateStripeSubscriptionModal';
import { getCancellationReasonLabel } from '@/features/contracts/constants/cancellationReasons';
import { OfferContractModal } from '@/features/contracts/components/OfferContractModal';
import { EditContractInfoModal } from '@/features/contracts/components/EditContractInfoModal';
import { RequestCancellationModal } from '@/features/contracts/components/RequestCancellationModal';
import { SendSavAccessEmailModal } from '@/features/contracts/components/SendSavAccessEmailModal';
import { useIsContractOffered } from '@/features/contracts/hooks/useContracts';
import { useAuthStore } from '@/Stores/auth-v2-store';
import { normalizeNameSpaces } from '@/utils/nameHelpers';

interface ContractDetailPageProps {
  contractId?: number;
  onBack?: () => void;
  className?: string;
  embedded?: boolean;
}

export function ContractDetailPage({ contractId: contractIdProp, onBack, className, embedded }: ContractDetailPageProps) {
  const { id } = useParams<{ id: string }>();
  const effectiveId = contractIdProp?.toString() || id;
  const parsedId = effectiveId ? parseInt(effectiveId) : undefined;
  const navigate = useNavigate();
  const { data: contract, isLoading, error } = useContract(parsedId);
  const { data: society } = useSociety(contract?.warranty?.societyId);
  const wording = useVendorWording(contract?.warranty?.societyId);
  const { data: isOffered } = useIsContractOffered(parsedId);
  const { currentUser } = useAuthStore();

  // Actions states
  const [actionsOpen, setActionsOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [offerModalOpen, setOfferModalOpen] = useState(false);
  const [requestCancelModalOpen, setRequestCancelModalOpen] = useState(false);
  const [reactivateModalOpen, setReactivateModalOpen] = useState(false);
  const [savAccessModalOpen, setSavAccessModalOpen] = useState(false);
  const actionsRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!actionsOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (actionsRef.current && !actionsRef.current.contains(e.target as Node)) {
        setActionsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [actionsOpen]);

  // Auto-open cancel modal when cancellation is requested (internal users only)
  useEffect(() => {
    if (contract?.cancelationRequested && contract.idStatus !== 3 && currentUser?.type === 'INTERNAL') {
      setCancelModalOpen(true);
    }
  }, [contract?.cancelationRequested, contract?.idStatus, currentUser?.type]);

  const canCancel = contract && contract.idStatus !== 3;
  // Contrat annulé sans remboursement à l'annulation (prélèvement encore en cours,
  // oubli) : on peut encore rembourser son dernier paiement Stripe.
  const canRefundCancelled = !!contract && contract.idStatus === 3 && !!contract.monthlyPaymentId;
  const canOffer = contract && contract.idStatus !== 3 && !isOffered;
  const hasSav = contract?.savs && contract.savs.length > 0;
  const canRequestCancellation = contract && contract.idStatus === 1 && !contract.cancelationRequested && !hasSav;
  const canReactivateStripeSubscription =
    !!contract &&
    contract.idStatus === 3 &&
    !!contract.monthlyPaymentId?.startsWith('sub_') &&
    ['canceled_manual', 'canceled_undone', 'canceled_unpaid'].includes(contract.monthlyPaymentStatus || '');

  const headerActions = (
    <div className="tw-relative" ref={actionsRef}>
      <button
        onClick={() => setActionsOpen(!actionsOpen)}
        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium tw-text-white tw-bg-white/20 tw-border tw-border-white/30 hover:tw-bg-white/30 tw-transition-colors"
      >
        <span className="tw-hidden md:tw-inline">Actions</span>
        <MoreVertical className="tw-w-4 tw-h-4" />
      </button>
      {actionsOpen && (
        <div className="tw-absolute tw-right-0 tw-mt-1 tw-w-72 tw-bg-white tw-rounded-md tw-shadow-lg tw-border tw-border-gray-200 tw-z-50 tw-py-1">
          <Can permission="contract.list">
            <button
              onClick={() => { setActionsOpen(false); setSavAccessModalOpen(true); }}
              className="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-text-left hover:tw-bg-gray-50 tw-transition-colors tw-text-gray-700"
            >
              <Siren className="tw-w-4 tw-h-4 tw-flex-shrink-0" />
              Envoyer les infos au client pour déclarer une panne
            </button>
          </Can>
          <Can permission="contract.*">
            <button
              onClick={() => { setActionsOpen(false); setEditModalOpen(true); }}
              className="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-text-left hover:tw-bg-gray-50 tw-transition-colors tw-text-gray-700"
            >
              <Edit2 className="tw-w-4 tw-h-4" />
              Modifier
            </button>
          </Can>
          <Can permission="contract.offer">
            <button
              onClick={() => { setActionsOpen(false); setOfferModalOpen(true); }}
              disabled={!canOffer}
              className="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-text-left hover:tw-bg-gray-50 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-text-blue-600"
            >
              <Gift className="tw-w-4 tw-h-4" />
              {isOffered ? 'Contrat déjà offert' : 'Offrir le contrat'}
            </button>
          </Can>
          <Can permission="contract.cancel">
            <button
              onClick={() => { setActionsOpen(false); setCancelModalOpen(true); }}
              disabled={!canCancel}
              className="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-text-left hover:tw-bg-gray-50 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-text-red-600"
            >
              <XCircle className="tw-w-4 tw-h-4" />
              {contract?.idStatus === 3 ? 'Contrat déjà annulé' : 'Annuler le contrat'}
            </button>
            {canRefundCancelled && (
              <button
                onClick={() => { setActionsOpen(false); setRefundModalOpen(true); }}
                className="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-text-left hover:tw-bg-gray-50 tw-transition-colors tw-text-affinicar-primary"
              >
                <Banknote className="tw-w-4 tw-h-4" />
                Rembourser le dernier paiement
              </button>
            )}
          </Can>
          <Can permission="contract.*">
            {canReactivateStripeSubscription && (
              <button
                onClick={() => { setActionsOpen(false); setReactivateModalOpen(true); }}
                className="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-text-left hover:tw-bg-gray-50 tw-transition-colors tw-text-green-600"
              >
                <RefreshCw className="tw-w-4 tw-h-4" />
                Réactiver la souscription Stripe
              </button>
            )}
          </Can>
          <Can permission="contract.request_cancellation">
            {hasSav ? (
              <span className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-text-gray-400">
                <AlertTriangle className="tw-w-4 tw-h-4" />
                Annulation impossible — SAV en cours
              </span>
            ) : (
              <button
                onClick={() => { setActionsOpen(false); setRequestCancelModalOpen(true); }}
                disabled={!canRequestCancellation}
                className="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-text-left hover:tw-bg-gray-50 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-text-orange-600"
              >
                <XCircle className="tw-w-4 tw-h-4" />
                {contract?.cancelationRequested ? 'Annulation déjà demandée' : "Demander l'annulation"}
              </button>
            )}
          </Can>
        </div>
      )}
    </div>
  );
  const { data: fullWarranty } = useWarrantyDetails(contract?.warranty?.uuid);
  const isMobilePdfView = useBreakpoint() !== 'lg';
  const { data: pdfBlob, isLoading: isPdfLoading, error: pdfError } = useContractPdfBlob(
    contract?.idDocument ? (effectiveId ? parseInt(effectiveId) : undefined) : undefined,
    !isMobilePdfView,
  );
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  const queryClient = useQueryClient();

  const [sigRetryForm, setSigRetryForm] = useState<{
    email: string; phone: string; firstname: string; lastname: string;
  } | null>(null);
  const [sigRetryLoading, setSigRetryLoading] = useState(false);
  const [sigRetryError, setSigRetryError] = useState<string | null>(null);
  const [sigRetrySuccess, setSigRetrySuccess] = useState(false);

  // Payment alert states & handlers
  const [paymentDirectLink, setPaymentDirectLink] = useState<string | null>(null);
  const [paymentResendOpen, setPaymentResendOpen] = useState(false);
  const [isRegeneratingPayment, setIsRegeneratingPayment] = useState(false);
  const [isResendingPayment, setIsResendingPayment] = useState(false);
  // Confirmation transitoire affichée DANS l'alerte rouge après un paiement
  // direct réussi, le temps que le webhook Stripe mette à jour le statut.
  const [paymentJustSubmitted, setPaymentJustSubmitted] = useState(false);

  const handleDirectPayment = async () => {
    if (!effectiveId) return;
    setIsRegeneratingPayment(true);
    try {
      const result = await contractApi.regeneratePaymentLink(parseInt(effectiveId));
      if (result.success && result.paymentLink) {
        setPaymentDirectLink(result.paymentLink);
      } else {
        toast.error(result.message || 'Impossible de générer le lien de paiement');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Erreur lors de la génération du lien');
    } finally {
      setIsRegeneratingPayment(false);
    }
  };

  const handleResendPaymentConfirm = async (email: string, phone: string) => {
    if (!effectiveId) return;
    setIsResendingPayment(true);
    try {
      const result = await contractApi.resendPaymentNotification(parseInt(effectiveId), email, phone);
      if (result.success) {
        toast.success('Lien de paiement renvoyé avec succès');
      } else {
        toast.error(result.message || 'Échec de l\'envoi');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Erreur lors de l\'envoi');
    } finally {
      setIsResendingPayment(false);
      setPaymentResendOpen(false);
    }
  };

  const handlePaymentSuccess = () => {
    if (effectiveId) {
      queryClient.invalidateQueries({ queryKey: ['payment-details', parseInt(effectiveId)] });
      queryClient.invalidateQueries({ queryKey: ['contract', parseInt(effectiveId)] });
    }
    // Affiche un sous-bloc vert DANS l'alerte rouge en attendant que le
    // webhook Stripe mette à jour le statut du contrat.
    setPaymentJustSubmitted(true);
  };

  // Reset du flag de confirmation paiement quand le statut a été mis à jour
  // par le webhook (= l'alerte rouge n'est plus pertinente).
  // NB: doit être déclaré avant les early returns ci-dessous pour respecter
  // la règle des hooks React.
  useEffect(() => {
    const stillPending =
      !!contract?.monthlyPaymentId &&
      contract?.monthlyPaymentStatus !== 'succeeded' &&
      contract?.monthlyPaymentStatus !== 'payment_method_updated';
    if (!stillPending && paymentJustSubmitted) {
      setPaymentJustSubmitted(false);
    }
  }, [contract?.monthlyPaymentId, contract?.monthlyPaymentStatus, paymentJustSubmitted]);

  const handleBack = onBack || (() => navigate(-1));

  const handleDownloadPdf = async () => {
    if (!effectiveId) return;
    setIsDownloadingPdf(true);
    try {
      const blob = await contractApi.downloadDocument(parseInt(effectiveId));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contrat-${contract?.label || effectiveId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // Erreur silencieuse
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const handleRetrySignature = async () => {
    if (!effectiveId || !sigRetryForm) return;
    setSigRetryLoading(true);
    setSigRetryError(null);
    setSigRetrySuccess(false);
    try {
      // Nettoyage des espaces avant envoi : YouSign rejette les espaces en début/fin
      // ou multiples sur first_name/last_name.
      const payload = {
        ...sigRetryForm,
        firstname: normalizeNameSpaces(sigRetryForm.firstname),
        lastname: normalizeNameSpaces(sigRetryForm.lastname),
      };
      await contractApi.retrySignature(parseInt(effectiveId), payload);
      setSigRetrySuccess(true);
      setSigRetryForm(null);
      queryClient.invalidateQueries({ queryKey: ['contract', parseInt(effectiveId)] });
    } catch (err: any) {
      setSigRetryError(err?.response?.data?.message || err?.message || 'Erreur lors de la relance');
    } finally {
      setSigRetryLoading(false);
    }
  };

  const handleRetryPdf = () => {
    if (effectiveId) {
      queryClient.invalidateQueries({ queryKey: ['contract-pdf', parseInt(effectiveId)] });
    }
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleUploadSignedDocument = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !effectiveId) return;
    setIsUploading(true);
    try {
      await contractApi.uploadSignedDocument(parseInt(effectiveId), file);
      toast.success('Contrat signé uploadé avec succès');
      queryClient.invalidateQueries({ queryKey: ['contract-pdf', parseInt(effectiveId)] });
      queryClient.invalidateQueries({ queryKey: ['contract', parseInt(effectiveId)] });
      queryClient.invalidateQueries({ queryKey: ['audit-logs'] });
    } catch {
      toast.error('Erreur lors de l\'upload du contrat signé');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (isLoading) {
    return (
      <div className="tw-min-h-screen tw-bg-gray-50 tw-px-6 tw-py-4 tw-space-y-4 tw-animate-pulse">
        <div className="tw-h-5 tw-w-40 tw-bg-gray-200 tw-rounded" />
        <div className="tw-h-28 tw-bg-gray-200 tw-rounded-xl" />
        <div className="tw-h-10 tw-bg-gray-200 tw-rounded-lg" />
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-4">
          <div className="lg:tw-col-span-2 tw-space-y-4">
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="tw-h-36 tw-bg-gray-200 tw-rounded-xl" />
              ))}
            </div>
          </div>
          <div className="tw-h-96 tw-bg-gray-200 tw-rounded-xl" />
        </div>
      </div>
    );
  }

  if (error || !contract) {
    return (
      <EmptyState
        icon={<AlertTriangle className="tw-w-12 tw-h-12" />}
        title="Contrat non trouvé"
        description="Ce contrat n'existe pas ou une erreur est survenue."
        action={{ label: 'Retour à la liste', onClick: handleBack }}
      />
    );
  }

  const beneficiaryName = contract.beneficiary
    ? formatFullName(contract.beneficiary.firstName, contract.beneficiary.lastName)
    : '—';

  const durationLabel = contract.duration ? formatDuration(contract.duration) : '—';
  const hasStripePayment = !!contract.monthlyPaymentId;
  const isPaymentInitialized = contract.monthlyPaymentStatus === 'initialized';
  const shouldShowPaymentBanner =
    hasStripePayment
    && (contract.monthlyPaymentStatus === 'initialized'
      || contract.monthlyPaymentStatus === 'failed');
  const isHandwrittenSignature = contract.signatureType === 2;
  const isElectronicSignature = contract.signatureType === 1;

  const beneficiaryAddress = contract.beneficiary
    ? formatAddress(contract.beneficiary.address, contract.beneficiary.address2, contract.beneficiary.cp, contract.beneficiary.city)
    : null;

  /**
   * Suffixe d'état du contrat lié, dérivé de son statut réel : 3 = annulé,
   * 4 = suspendu. Écrire « (annulé) » en dur mentirait sur un contrat qui ne l'est
   * pas — un initial peut être suspendu sans avoir été annulé.
   */
  const linkedContractStateSuffix =
    contract.linkedContract?.idStatus === 3
      ? ' (annulé)'
      : contract.linkedContract?.idStatus === 4
        ? ' (suspendu)'
        : '';

  return (
    <div className={embedded ? (className || '') : `tw-min-h-screen tw-bg-gray-50 ${className || ''}`}>
      <div className={embedded ? 'tw-space-y-4' : 'tw-px-6 tw-space-y-4'}>

        {/* Header */}
        <DetailHeader
          type="contract"
          title={contract.label || '—'}
          status={{ label: contractStatusLabel(contract.idStatus, contract.monthlyPaymentId, contract.monthlyPaymentStatus, contract.cancelationRequested), variant: contractStatusToVariant(contract.idStatus, contract.monthlyPaymentId, contract.monthlyPaymentStatus, contract.cancelationRequested) }}
          breadcrumb={[
            onBack ? { label: 'Contrats', onClick: onBack } : { label: 'Contrats', href: '/contracts' },
            { label: contract.label || 'Détail' },
          ]}
          actions={headerActions}
          metadata={[
            { label: wording.singular, value: contract.warranty?.name || '—' },
            { label: 'Durée', value: durationLabel },
            (() => {
              const price = getContractDisplayPrice({
                sellingPrice: contract.sellingPrice || 0,
                duration: contract.duration,
                monthlyPriceClient: contract.monthlyPriceClient,
              });
              return price.kind === 'monthly'
                ? { label: 'Prix mensuel TTC', value: `${formatPrice(price.monthly)} / mois` }
                : { label: 'Prix TTC', value: formatPrice(price.total) };
            })(),
            {
              label: 'Créé par',
              value: contract.creator ? `${contract.creator.firstName} ${contract.creator.lastName}` : '—',
            },
            {
              label: 'Date de création',
              value: contract.dateCreation ? formatDate(contract.dateCreation) : '—',
            },
            {
              label: 'Date de début',
              value: contract.dateSouscription ? formatDate(contract.dateSouscription) : '—',
            },
          ]}
        />

        {/* Badge UpSell */}
        {!!contract.isUpsell && (
          <div className="tw-flex tw-items-center tw-gap-2">
            <span className="tw-inline-flex tw-items-center tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-bold tw-bg-indigo-100 tw-text-indigo-800 tw-border tw-border-indigo-200">
              Contrat UpSell
            </span>
          </div>
        )}

        {/* Encart assistance — pleine largeur, même gabarit que l'encart « contrat lié ».
            Teal et non orange : l'orange est déjà porté par l'encart de contrat remplacé
            par un UpSell, une information de cycle de vie. Sur un contrat qui cumule les
            deux, deux bandeaux identiques auraient dit des choses de nature opposée.

            Le libellé vient du SNAPSHOT du contrat, jamais du référentiel : si la formule
            est renommée ou retirée de la garantie, le contrat continue d'afficher ce qui
            a été vendu au client. */}
        {contract.assistanceFormulaId != null && (
          <div className="tw-rounded-xl tw-border tw-p-4 tw-shadow-sm tw-flex tw-items-center tw-gap-3 tw-bg-teal-50 tw-border-teal-200">
            <Siren className="tw-w-5 tw-h-5 tw-text-teal-700 tw-flex-shrink-0" />
            <span className="tw-text-sm tw-font-semibold tw-text-teal-800">
              {assistanceSentence(contract.assistanceBenefits, contract.assistanceLabel)}
            </span>
          </div>
        )}

        {/*
          Suffixe d'état du contrat lié, dérivé de son STATUT réel : 3 = annulé,
          4 = suspendu (cf. ContractStatus). Afficher « (annulé) » en dur mentirait
          sur un contrat qui ne l'est pas.
        */}
        {/* Encart contrat lié (upsell ↔ suspendu) */}
        {contract.linkedContract && (
          <div className={`tw-rounded-xl tw-border tw-p-4 tw-shadow-sm tw-flex tw-items-center tw-justify-between ${
            contract.linkedContract.relation === 'upsell'
              ? 'tw-bg-orange-50 tw-border-orange-200'
              : 'tw-bg-indigo-50 tw-border-indigo-200'
          }`}>
            <div>
              <span className={`tw-text-sm tw-font-semibold ${
                contract.linkedContract.relation === 'upsell' ? 'tw-text-orange-800' : 'tw-text-indigo-800'
              }`}>
                {contract.linkedContract.relation === 'upsell'
                  ? 'Ce contrat a été remplacé par un contrat UpSell'
                  : `Ce contrat a été vendu via le service Affinicar Upsell et remplace le contrat initial ${contract.linkedContract.label}${linkedContractStateSuffix}.`}
              </span>
              {/* Le label est désormais DANS la phrase pour la branche « ce contrat est
                  l'upsell ». L'autre branche le garde à côté, sa phrase étant inchangée. */}
              {contract.linkedContract.relation === 'upsell' && (
                <span className="tw-text-sm tw-text-gray-600 tw-ml-2">
                  {contract.linkedContract.label}
                  {linkedContractStateSuffix}
                </span>
              )}
            </div>
            <Link
              to={`/contracts/${contract.linkedContract.id}`}
              className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-rounded-lg tw-no-underline tw-transition-colors tw-border
                tw-text-indigo-700 tw-border-indigo-200 hover:tw-bg-indigo-100"
            >
              Voir le contrat &rarr;
            </Link>
          </div>
        )}

        {/* Contenu principal */}
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-4">

          {/* Colonne gauche */}
          <div className="lg:tw-col-span-2 tw-space-y-4">

            {/* Alerte paiement — paiement non effectué (signature manuscrite ou signature électronique déjà signée) */}
            {contract.idStatus !== 3 && shouldShowPaymentBanner && (isHandwrittenSignature || (isElectronicSignature && contract.signatureStatus === 'signed')) && (
              <div className="tw-rounded-xl tw-border tw-p-4 tw-shadow-sm tw-bg-red-50 tw-border-red-200">
                <div className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center tw-gap-3">
                  <AlertTriangle className="tw-w-5 tw-h-5 tw-flex-shrink-0 tw-text-red-500" />
                  <div className="tw-flex-1 tw-min-w-0">
                    <h4 className="tw-text-sm tw-font-semibold tw-m-0 tw-text-red-800">
                      {contract.monthlyPaymentStatus === 'failed' ? 'Échec de paiement' : 'Le paiement n\'a pas été effectué'}
                    </h4>
                    <p className="tw-text-xs tw-mt-0.5 tw-mb-0 tw-text-red-600">
                      {contract.monthlyPaymentStatus === 'failed'
                        ? (() => {
                            const reminderDate = contract.monthlyPaymentReminderDate ? new Date(contract.monthlyPaymentReminderDate) : null;
                            const reminder = contract.monthlyPaymentReminder || 0;
                            if (reminderDate && reminder > 0) {
                              const totalDays = reminder === 1 ? 9 : 2;
                              const deadline = new Date(reminderDate.getTime() + totalDays * 24 * 3600_000);
                              const remaining = Math.max(0, Math.ceil((deadline.getTime() - Date.now()) / (24 * 3600_000)));
                              return `Le prélèvement a échoué. Le bénéficiaire dispose de ${remaining} jour${remaining > 1 ? 's' : ''} pour régulariser sa situation.`;
                            }
                            return 'Le prélèvement a échoué. Le bénéficiaire doit régulariser sa situation.';
                          })()
                        : 'Le bénéficiaire n\'a pas encore réglé le paiement de ce contrat.'}
                    </p>
                  </div>
                  {!paymentJustSubmitted && (
                    <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2 sm:tw-flex-shrink-0 tw-w-full sm:tw-w-auto">
                      {contract.monthlyPaymentStatus !== 'failed' && (
                        <button
                          onClick={handleDirectPayment}
                          disabled={isRegeneratingPayment}
                          className="tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm tw-font-medium tw-text-red-700 tw-bg-white tw-border tw-border-red-300 hover:tw-bg-red-50 tw-transition-colors tw-shadow-sm disabled:tw-opacity-50 tw-w-full sm:tw-w-auto"
                        >
                          {isRegeneratingPayment ? (
                            <Loader2 className="tw-w-3.5 tw-h-3.5 tw-animate-spin" />
                          ) : (
                            <CreditCard className="tw-w-3.5 tw-h-3.5" />
                          )}
                          Effectuer le paiement en direct
                        </button>
                      )}
                      <button
                        onClick={() => setPaymentResendOpen(true)}
                        className="tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm tw-font-medium tw-text-white tw-bg-red-600 hover:tw-bg-red-700 tw-transition-colors tw-shadow-sm tw-w-full sm:tw-w-auto tw-text-center"
                      >
                        <Mail className="tw-w-3.5 tw-h-3.5 tw-flex-shrink-0" />
                        {contract.monthlyPaymentStatus === 'failed'
                          ? 'Renvoyer le lien de mise à jour du moyen de paiement'
                          : 'Renvoyer le lien de paiement'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Confirmation transitoire après paiement direct réussi
                    (le statut côté API se met à jour quand le webhook Stripe arrive) */}
                {paymentJustSubmitted && (
                  <div className="tw-mt-3 tw-flex tw-items-start tw-gap-2 tw-rounded-md tw-border tw-border-green-300 tw-bg-green-50 tw-px-3 tw-py-2">
                    <CheckCircle2 className="tw-w-4 tw-h-4 tw-text-green-600 tw-flex-shrink-0 tw-mt-0.5" />
                    <p className="tw-text-xs tw-text-green-800 tw-font-medium tw-mb-0">
                      Votre paiement a bien été enregistré, il se mettra à jour dans quelques instants.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Alerte signature (masquée pour les contrats upsell — signature gérée via Yousign upsell, pas le flux standard) */}
            {contract.idStatus !== 3 && !contract.isUpsell && contract.signatureStatus && contract.signatureStatus !== 'signed' && contract.signatureStatus !== 'handwritten' && (
              <div className={`tw-rounded-xl tw-border tw-p-4 tw-shadow-sm ${
                contract.signatureStatus === 'failed' ? 'tw-bg-red-50 tw-border-red-200' :
                contract.signatureStatus === 'canceled' ? 'tw-bg-orange-50 tw-border-orange-200' :
                isElectronicSignature && isPaymentInitialized ? 'tw-bg-red-50 tw-border-red-200' :
                'tw-bg-yellow-50 tw-border-yellow-200'
              }`}>
                <div className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center tw-gap-3">
                  <AlertTriangle className={`tw-w-5 tw-h-5 tw-flex-shrink-0 ${
                    contract.signatureStatus === 'failed' ? 'tw-text-red-500' :
                    contract.signatureStatus === 'canceled' ? 'tw-text-orange-500' :
                    isElectronicSignature && isPaymentInitialized ? 'tw-text-red-500' :
                    'tw-text-yellow-500'
                  }`} />
                  <div className="tw-flex-1 tw-min-w-0">
                    <h4 className={`tw-text-sm tw-font-semibold tw-m-0 ${
                      contract.signatureStatus === 'failed' ? 'tw-text-red-800' :
                      contract.signatureStatus === 'canceled' ? 'tw-text-orange-800' :
                      isElectronicSignature && isPaymentInitialized ? 'tw-text-red-800' :
                      'tw-text-yellow-800'
                    }`}>
                      {contract.signatureStatus === 'failed' && 'La signature électronique n\'a pas pu être créée'}
                      {contract.signatureStatus === 'canceled' && 'La signature a expiré ou a été annulée'}
                      {contract.signatureStatus === 'pending' && (
                        isElectronicSignature && isPaymentInitialized
                          ? 'En attente de paiement et de signature par le bénéficiaire'
                          : 'En attente de signature par le bénéficiaire'
                      )}
                    </h4>
                    <p className={`tw-text-xs tw-mt-0.5 tw-mb-0 ${
                      contract.signatureStatus === 'failed' ? 'tw-text-red-600' :
                      contract.signatureStatus === 'canceled' ? 'tw-text-orange-600' :
                      isElectronicSignature && isPaymentInitialized ? 'tw-text-red-600' :
                      'tw-text-yellow-600'
                    }`}>
                      {contract.signatureStatus === 'failed' && 'Vérifiez les informations du bénéficiaire puis relancez la signature.'}
                      {contract.signatureStatus === 'canceled' && 'Vous pouvez modifier les informations si nécessaire et relancer la signature.'}
                      {contract.signatureStatus === 'pending' && (
                        <>
                          {contract.signature?.recipientEmail
                            ? `Email envoyé à ${contract.signature.recipientEmail}`
                            : 'Le lien de signature a été envoyé au bénéficiaire.'}
                          {(contract.signature as any)?.createdAt && (
                            <span className="tw-ml-2 tw-text-gray-500">
                              ({formatRelativeDate((contract.signature as any).createdAt)})
                            </span>
                          )}
                        </>
                      )}
                    </p>
                  </div>
                  {!sigRetryForm && (
                    <button
                      onClick={() => setSigRetryForm({
                        firstname: contract.signature?.recipientFirstname || contract.beneficiary?.firstName || '',
                        lastname: contract.signature?.recipientLastname || contract.beneficiary?.lastName || '',
                        email: contract.signature?.recipientEmail || contract.beneficiary?.email || '',
                        phone: contract.signature?.recipientPhone || contract.beneficiary?.phoneNumber || '',
                      })}
                      className="sm:tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm tw-font-medium tw-text-white tw-bg-affinicar-primary hover:tw-bg-affinicar-dark tw-transition-colors tw-shadow-sm tw-w-full sm:tw-w-auto"
                    >
                      <Send className="tw-w-3.5 tw-h-3.5 tw-flex-shrink-0" />
                      {contract.signatureStatus === 'failed' ? 'Créer la signature' :
                       contract.signatureStatus === 'pending' && isElectronicSignature && isPaymentInitialized ? 'Relancer le paiement et la signature' :
                       contract.signatureStatus === 'pending' ? 'Renvoyer la signature' :
                       'Relancer la signature'}
                    </button>
                  )}
                </div>

                {sigRetrySuccess && (
                  <p className="tw-text-xs tw-text-green-700 tw-mt-2 tw-mb-0 tw-font-medium">
                    Signature relancée avec succès ! Le bénéficiaire va recevoir un nouveau lien.
                  </p>
                )}

                {sigRetryError && (
                  <p className="tw-text-xs tw-text-red-700 tw-mt-2 tw-mb-0">
                    Erreur : {sigRetryError}
                  </p>
                )}

                {sigRetryForm && (
                  <div className="tw-mt-3 tw-space-y-2">
                    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-2">
                      <div>
                        <label className="tw-text-xs tw-text-gray-600 tw-block tw-mb-0.5">Prénom</label>
                        <input
                          type="text"
                          value={sigRetryForm.firstname}
                          onChange={(e) => setSigRetryForm({ ...sigRetryForm, firstname: e.target.value })}
                          className="tw-w-full tw-border tw-border-gray-300 tw-rounded-lg tw-px-3 tw-py-1.5 tw-text-sm"
                        />
                      </div>
                      <div>
                        <label className="tw-text-xs tw-text-gray-600 tw-block tw-mb-0.5">Nom</label>
                        <input
                          type="text"
                          value={sigRetryForm.lastname}
                          onChange={(e) => setSigRetryForm({ ...sigRetryForm, lastname: e.target.value })}
                          className="tw-w-full tw-border tw-border-gray-300 tw-rounded-lg tw-px-3 tw-py-1.5 tw-text-sm"
                        />
                      </div>
                    </div>
                    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-2">
                      <div>
                        <label className="tw-text-xs tw-text-gray-600 tw-block tw-mb-0.5">Email</label>
                        <input
                          type="email"
                          value={sigRetryForm.email}
                          onChange={(e) => setSigRetryForm({ ...sigRetryForm, email: e.target.value })}
                          className="tw-w-full tw-border tw-border-gray-300 tw-rounded-lg tw-px-3 tw-py-1.5 tw-text-sm"
                        />
                      </div>
                      <div>
                        <label className="tw-text-xs tw-text-gray-600 tw-block tw-mb-0.5">Téléphone</label>
                        <input
                          type="tel"
                          value={sigRetryForm.phone}
                          onChange={(e) => setSigRetryForm({ ...sigRetryForm, phone: e.target.value })}
                          className="tw-w-full tw-border tw-border-gray-300 tw-rounded-lg tw-px-3 tw-py-1.5 tw-text-sm"
                        />
                      </div>
                    </div>
                    <div className="tw-flex tw-gap-2 tw-mt-2 tw-justify-end">
                      <button
                        onClick={() => { setSigRetryForm(null); setSigRetryError(null); }}
                        className="tw-px-3 tw-py-1.5 tw-rounded-lg tw-text-sm tw-text-gray-600 hover:tw-bg-gray-100 tw-transition-colors"
                      >
                        Annuler
                      </button>
                      <button
                        onClick={handleRetrySignature}
                        disabled={sigRetryLoading}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm tw-font-medium tw-text-white tw-bg-affinicar-primary hover:tw-bg-affinicar-dark tw-transition-colors disabled:tw-opacity-50"
                      >
                        {sigRetryLoading ? (
                          <Loader2 className="tw-w-3.5 tw-h-3.5 tw-animate-spin" />
                        ) : (
                          <Send className="tw-w-3.5 tw-h-3.5" />
                        )}
                        {contract.signatureStatus === 'failed' ? 'Créer la signature' :
                         isElectronicSignature && isPaymentInitialized ? 'Relancer le paiement et la signature' :
                         'Relancer la signature'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Motif d'annulation */}
            {contract.idStatus === 3 && (
              <div className="tw-rounded-xl tw-border tw-p-4 tw-shadow-sm tw-bg-red-50 tw-border-red-200">
                <div className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-start tw-gap-3">
                  <Ban className="tw-w-5 tw-h-5 tw-flex-shrink-0 tw-text-red-500 sm:tw-mt-0.5" />
                  <div className="tw-flex-1 tw-min-w-0">
                    <h4 className="tw-text-sm tw-font-semibold tw-m-0 tw-text-red-800">
                      Contrat annulé
                    </h4>
                    <p className="tw-text-xs tw-mt-0.5 tw-mb-0 tw-text-red-600">
                      {!contract.monthlyPaymentId && 'Annulation manuelle'}
                      {contract.monthlyPaymentId && contract.monthlyPaymentStatus === 'canceled_manual' && 'Annulation manuelle'}
                      {contract.monthlyPaymentId && contract.monthlyPaymentStatus === 'canceled_undone' && 'Paiement non finalisé dans les délais'}
                      {contract.monthlyPaymentId && contract.monthlyPaymentStatus === 'canceled_unpaid' && 'Impayés — prélèvements en échec'}
                      {contract.monthlyPaymentId && contract.monthlyPaymentStatus === 'canceled_dispute_lost' && 'Opposition au paiement'}
                      {contract.monthlyPaymentId && contract.monthlyPaymentStatus === 'payment_failed_twice' && 'Double échec de prélèvement'}
                      {contract.monthlyPaymentId && !['canceled_manual', 'canceled_undone', 'canceled_unpaid', 'canceled_dispute_lost', 'payment_failed_twice'].includes(contract.monthlyPaymentStatus || '') && 'Annulation manuelle'}
                      {contract.dateCanceled && (
                        <span className="tw-ml-2 tw-text-gray-500">
                          (le {formatDate(contract.dateCanceled)})
                        </span>
                      )}
                    </p>
                    {contract.cancelationReason && (
                      <p className="tw-text-xs tw-mt-1 tw-mb-0 tw-text-red-700">
                        <span className="tw-font-medium">Motif :</span> {getCancellationReasonLabel(contract.cancelationReason)}
                      </p>
                    )}
                    {contract.cancelationRequestedAt && (
                      <p className="tw-text-xs tw-mt-1 tw-mb-0 tw-text-red-700">
                        <span className="tw-font-medium">Demandé le :</span> {formatDate(contract.cancelationRequestedAt)}
                      </p>
                    )}
                    {contract.cancelationDetails && (
                      <p className="tw-text-xs tw-mt-1 tw-mb-0 tw-text-red-700 tw-whitespace-pre-line">
                        <span className="tw-font-medium">Détails :</span> {contract.cancelationDetails}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Barre de progression temporelle */}
            {contract.idStatus !== 3 && (
              <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm tw-p-4">
                <ProgressTimeline
                  startDate={contract.dateSouscription}
                  endDate={contract.dateEnd}
                  creationDate={contract.dateCreation}
                />
              </div>
            )}

            {/* Bénéficiaire + Garant */}
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
              <InfoCard title="Bénéficiaire" icon={<User className="tw-w-4 tw-h-4" />}>
                {contract.beneficiary ? (
                  <div className="tw-space-y-0.5">
                    <InfoRow label="Nom" value={beneficiaryName} icon={<User className="tw-w-3.5 tw-h-3.5" />} />
                    <InfoRow
                      label="Email"
                      value={contract.beneficiary.email || '—'}
                      icon={<Mail className="tw-w-3.5 tw-h-3.5" />}
                      href={contract.beneficiary.email ? `mailto:${contract.beneficiary.email}` : undefined}
                    />
                    <InfoRow
                      label="Téléphone"
                      value={formatPhone(contract.beneficiary.phoneNumber)}
                      icon={<Phone className="tw-w-3.5 tw-h-3.5" />}
                      href={contract.beneficiary.phoneNumber ? `tel:${contract.beneficiary.phoneNumber}` : undefined}
                    />
                    <InfoRow
                      label="Adresse"
                      value={beneficiaryAddress || '—'}
                      icon={<MapPin className="tw-w-3.5 tw-h-3.5" />}
                    />
                  </div>
                ) : (
                  <p className="tw-text-sm tw-text-gray-400 tw-italic">Aucune information</p>
                )}
              </InfoCard>

              <InfoCard title="Garant" icon={<Building2 className="tw-w-4 tw-h-4" />} compact>
                {(contract.society || society) ? (
                  <div className="tw-space-y-0.5">
                    <InfoRow label="Société" value={contract.society?.name || society?.name || '—'} icon={<Building2 className="tw-w-3.5 tw-h-3.5" />} />
                    <InfoRow
                      label="Email"
                      value={society?.email || '—'}
                      icon={<Mail className="tw-w-3.5 tw-h-3.5" />}
                      href={society?.email ? `mailto:${society.email}` : undefined}
                    />
                    <InfoRow
                      label="Téléphone"
                      value={formatPhone(society?.phone)}
                      icon={<Phone className="tw-w-3.5 tw-h-3.5" />}
                      href={society?.phone ? `tel:${society.phone}` : undefined}
                    />
                    <InfoRow
                      label="Adresse"
                      value={[contract.society?.address || society?.address, `${contract.society?.cp || society?.cp || ''} ${contract.society?.city || society?.city || ''}`.trim()].filter(Boolean).join(', ') || '—'}
                      icon={<MapPin className="tw-w-3.5 tw-h-3.5" />}
                    />
                  </div>
                ) : (
                  <p className="tw-text-sm tw-text-gray-400 tw-italic">Aucune information</p>
                )}
              </InfoCard>
            </div>

            {/* Véhicule + Prestation */}
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
              <InfoCard title="Véhicule" icon={<Car className="tw-w-4 tw-h-4" />}>
                {contract.vehicle ? (
                  <div className="tw-space-y-0.5">
                    <InfoRow label="Immatriculation" value={contract.vehicle.registration || '—'} icon={<CreditCard className="tw-w-3.5 tw-h-3.5" />} />
                    <InfoRow label="Marque" value={contract.vehicle.brand || '—'} icon={<Factory className="tw-w-3.5 tw-h-3.5" />} />
                    <InfoRow label="Modèle" value={contract.vehicle.model || '—'} icon={<Car className="tw-w-3.5 tw-h-3.5" />} />
                    <InfoRow
                      label="Finition"
                      value={contract.vehicle.finishes}
                      icon={<Paintbrush className="tw-w-3.5 tw-h-3.5" />}
                      hide={!contract.vehicle.finishes || contract.vehicle.finishes === 'N/A'}
                    />
                    <InfoRow
                      label="Énergie"
                      value={contract.vehicle.fuel}
                      icon={<Fuel className="tw-w-3.5 tw-h-3.5" />}
                      hide={!contract.vehicle.fuel}
                    />
                    <InfoRow
                      label="Mise en circulation"
                      value={formatDate(contract.vehicle.entryIntoService)}
                      icon={<Calendar className="tw-w-3.5 tw-h-3.5" />}
                      hide={!contract.vehicle.entryIntoService}
                    />
                    <InfoRow
                      label="Kilométrage"
                      value={formatMileage(contract.vehicle.kmStart)}
                      icon={<Gauge className="tw-w-3.5 tw-h-3.5" />}
                      hide={!contract.vehicle.kmStart}
                    />
                    <InfoRow
                      label="Prix de vente"
                      value={contract.vehicle.price ? formatPrice(Number(contract.vehicle.price)) : '—'}
                      icon={<Euro className="tw-w-3.5 tw-h-3.5" />}
                      hide={!contract.vehicle.price}
                    />
                  </div>
                ) : (
                  <p className="tw-text-sm tw-text-gray-400 tw-italic">Aucune information</p>
                )}
              </InfoCard>

              <InfoCard title={wording.singular} icon={<Shield className="tw-w-4 tw-h-4" />}>
                <div className="tw-space-y-0.5">
                  <InfoRow
                    label="Type"
                    value={contract.warranty?.warrantyType !== undefined
                      ? WARRANTY_TYPE_LABELS[contract.warranty.warrantyType] || '—'
                      : '—'}
                    icon={<Shield className="tw-w-3.5 tw-h-3.5" />}
                  />
                  <InfoRow
                    label="Véhicule"
                    value={fullWarranty?.vehicleType !== undefined
                      ? VEHICLE_TYPE_LABELS[fullWarranty.vehicleType] || '—'
                      : '—'}
                    icon={<Car className="tw-w-3.5 tw-h-3.5" />}
                  />
                  <InfoRow
                    label="Durée"
                    value={contract.duration
                      ? (DURATION_LABELS[contract.duration as keyof typeof DURATION_LABELS] || `${contract.duration} mois`)
                      : '—'}
                    icon={<Clock className="tw-w-3.5 tw-h-3.5" />}
                  />
                  <InfoRow
                    label="Limite KM"
                    value={contract.warrantyKmLimit ? formatMileage(contract.warrantyKmLimit) : '—'}
                    icon={<Gauge className="tw-w-3.5 tw-h-3.5" />}
                  />
                  <InfoRow
                    label="Plafond"
                    value={contract.warrantyFinancialCap ? formatPrice(contract.warrantyFinancialCap) : '—'}
                    icon={<Euro className="tw-w-3.5 tw-h-3.5" />}
                  />
                  <InfoRow
                    label="Pièces"
                    value={fullWarranty?.parts ? `${fullWarranty.parts.length} pièce${fullWarranty.parts.length > 1 ? 's' : ''}` : '—'}
                    icon={<Wrench className="tw-w-3.5 tw-h-3.5" />}
                  />
                  <InfoRow
                    label="Rétrocession"
                    value={formatPrice(contract.warrantyTransferPrice)}
                    icon={<ArrowLeftRight className="tw-w-3.5 tw-h-3.5" />}
                    hide={!contract.isTransferPrice || !contract.warrantyTransferPrice}
                  />
                </div>
              </InfoCard>
            </div>

            {/* Documents supplémentaires */}
            {contract.contractDocuments && contract.contractDocuments.length > 0 && (
              <InfoCard title={`Documents (${contract.contractDocuments.length})`} icon={<FileText className="tw-w-4 tw-h-4" />}>
                <div className="tw-space-y-2">
                  {contract.contractDocuments.map((doc) => (
                    <div
                      key={doc.id}
                      onClick={() => contractApi.downloadContractDocument(doc.id, doc.fileName)}
                      className="tw-flex tw-items-center tw-justify-between tw-p-3 tw-rounded-lg tw-border tw-border-gray-100 tw-cursor-pointer tw-transition-all hover:tw-shadow-sm hover:tw-border-affinicar-primary/30 tw-group/doc"
                    >
                      <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
                        <FileText className="tw-w-4 tw-h-4 tw-text-gray-400 tw-flex-shrink-0" />
                        <span className="tw-text-sm tw-font-medium tw-text-gray-900 tw-truncate">{doc.name}</span>
                      </div>
                      <Download className="tw-w-4 tw-h-4 tw-text-gray-400 group-hover/doc:tw-text-affinicar-primary tw-transition-colors tw-flex-shrink-0" />
                    </div>
                  ))}
                </div>
              </InfoCard>
            )}

            {/* Paiement Stripe — visible dès qu'un paiement Stripe existe, mensualisé
                (sub_xxx) comme comptant en ligne (pi_xxx), quel que soit le statut
                courant : actif, en échec, annulé avec paiements historiques
                (canceled_*), ou complété. Exclut seulement les SetupIntent non
                confirmés (seti_xxx + mps = initialized) pour lesquels aucun paiement
                n'a jamais eu lieu.
                Un `startsWith('sub_')` était utilisé ici : il n'excluait pas seulement
                les seti_ visés, il masquait AUSSI tout le bloc sur les comptants. */}
            {!!contract.monthlyPaymentId
              && !(contract.monthlyPaymentId.startsWith('seti_') && contract.monthlyPaymentStatus === 'initialized') && (
              <PaymentDetailsCard contractId={contract.id} contractLabel={contract.label} signatureType={contract.signatureType} societyId={contract.warranty?.societyId} />
            )}

            {/* Remboursement client (annulation, ou report du 1er prélèvement sur un contrat
                actif) : rien tant qu'aucun n'a été demandé */}
            <ContractRefundsCard contractId={contract.id} />

            {/* Attestation de livraison */}
            <InfoCard title="Attestation de livraison" icon={<FileCheck className="tw-w-4 tw-h-4" />}>
              {contract.deliveryProof ? (
                <div
                  onClick={contract.deliveryProof.state === 'draft'
                    ? () => navigate(`/delivery-proofs/new?dpId=${contract.deliveryProof!.id}`)
                    : () => deliveryProofApi.downloadPdf(String(contract.deliveryProof!.id), `attestation-livraison-${contract.label}.pdf`)}
                  className="tw-flex tw-items-center tw-justify-between tw-p-3 tw-rounded-lg tw-border tw-border-gray-100 tw-transition-all tw-cursor-pointer hover:tw-shadow-sm hover:tw-border-affinicar-primary/30 tw-group/dp"
                >
                  <div>
                    <span className="tw-text-sm tw-font-medium tw-text-gray-900">
                      {contract.deliveryProof.dpNumber ? `N° ${contract.deliveryProof.dpNumber}` : 'Attestation'}
                    </span>
                    <span className="tw-text-sm tw-text-gray-500 tw-ml-3">
                      {formatDate(contract.deliveryProof.createdAt)}
                    </span>
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <StatusBadge
                      label={deliveryProofStateLabel(contract.deliveryProof.state)}
                      variant={deliveryProofStateToVariant(contract.deliveryProof.state)}
                    />
                    {contract.deliveryProof.state === 'draft' ? (
                      <ChevronRight className="tw-w-4 tw-h-4 tw-text-gray-400 group-hover/dp:tw-text-affinicar-primary tw-transition-colors" />
                    ) : (
                      <Download className="tw-w-4 tw-h-4 tw-text-gray-400 group-hover/dp:tw-text-affinicar-primary tw-transition-colors" />
                    )}
                  </div>
                </div>
              ) : (
                <div className="tw-text-center tw-py-4">
                  <p className="tw-text-sm tw-text-gray-400 tw-mb-2">Aucune attestation de livraison liée</p>
                  {contract.idStatus !== 3 && (
                    <Link
                      to={`/delivery-proofs/new?contractId=${contract.id}`}
                      className="tw-inline-flex tw-items-center tw-gap-1 tw-text-sm tw-font-medium tw-text-affinicar-primary hover:tw-underline tw-no-underline tw-transition-colors"
                    >
                      <Plus className="tw-w-3.5 tw-h-3.5" />
                      Créer une attestation
                    </Link>
                  )}
                </div>
              )}
            </InfoCard>

            {/* SAV */}
            {contract.savs && contract.savs.length > 0 && (
              <InfoCard title={`SAV (${contract.savs.length})`} icon={<Wrench className="tw-w-4 tw-h-4" />}>
                <div className="tw-space-y-2">
                  {contract.savs.map((sav: any) => (
                    <div
                      key={sav.id}
                      onClick={() => navigate(`/sav/${sav.id}`)}
                      className="tw-flex tw-items-center tw-justify-between tw-p-3 tw-rounded-lg tw-border tw-border-gray-100 tw-cursor-pointer tw-transition-all hover:tw-shadow-sm hover:tw-border-affinicar-primary/30 tw-group/sav"
                    >
                      <div>
                        <span className="tw-text-sm tw-font-medium tw-text-gray-900">
                          {sav.uniqueId ? `SAV-${sav.uniqueId}` : `#${sav.id}`}
                        </span>
                        <span className="tw-text-sm tw-text-gray-500 tw-ml-3">
                          {formatDate(sav.dateCreation)}
                        </span>
                      </div>
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <span className={`tw-px-2 tw-py-0.5 tw-inline-flex tw-text-xs tw-leading-5 tw-font-semibold tw-rounded-full ${getSavStatusColorClass(sav.status)}`}>
                          {getSavStatusLabel(sav.status)}
                        </span>
                        <ChevronRight className="tw-w-4 tw-h-4 tw-text-gray-400 group-hover/sav:tw-text-affinicar-primary tw-transition-colors" />
                      </div>
                    </div>
                  ))}
                </div>
              </InfoCard>
            )}
          </div>

          {/* Colonne droite : PDF (preview desktop, boutons mobile) */}
          <div>
            {contract.idDocument ? (
              isMobilePdfView ? (
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-4 tw-shadow-sm">
                  <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
                    <FileText className="tw-w-5 tw-h-5 tw-text-affinicar-primary tw-flex-shrink-0" />
                    <h3 className="tw-text-base tw-font-semibold tw-text-gray-900 tw-m-0">
                      Document contrat
                    </h3>
                  </div>
                  <div className="tw-flex tw-flex-col tw-gap-2">
                    <button
                      onClick={handleDownloadPdf}
                      disabled={isDownloadingPdf}
                      className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-3 tw-rounded-lg tw-text-sm tw-font-medium tw-text-white tw-bg-affinicar-primary hover:tw-bg-affinicar-dark tw-transition-colors tw-shadow-sm disabled:tw-opacity-50"
                    >
                      {isDownloadingPdf ? (
                        <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
                      ) : (
                        <Download className="tw-w-4 tw-h-4" />
                      )}
                      Télécharger le PDF
                    </button>
                    {contract.signatureType === 2 && (
                      <>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf"
                          onChange={handleUploadSignedDocument}
                          className="tw-hidden"
                        />
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isUploading}
                          className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-3 tw-rounded-lg tw-text-sm tw-font-medium tw-text-affinicar-primary tw-bg-white tw-border tw-border-affinicar-primary hover:tw-bg-affinicar-lighter tw-transition-colors disabled:tw-opacity-50"
                        >
                          {isUploading ? (
                            <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
                          ) : (
                            <Upload className="tw-w-4 tw-h-4" />
                          )}
                          Uploader le contrat signé
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="tw-sticky tw-top-20">
                  <PdfPreviewCard
                    pdfBlob={pdfBlob}
                    isLoading={isPdfLoading}
                    error={!!pdfError}
                    title="Document contrat"
                    documentName={contract.label}
                    onRetry={handleRetryPdf}
                    headerAction={
                      <div className="tw-flex tw-items-center tw-gap-1">
                        {contract.signatureType === 2 && (
                          <>
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept=".pdf"
                              onChange={handleUploadSignedDocument}
                              className="tw-hidden"
                            />
                            <button
                              onClick={() => fileInputRef.current?.click()}
                              disabled={isUploading}
                              className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-lg tw-text-sm tw-font-medium tw-text-affinicar-primary hover:tw-text-affinicar-dark hover:tw-bg-affinicar-lighter tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                              title="Uploader le contrat signé"
                            >
                              {isUploading ? (
                                <Loader2 className="tw-w-3.5 tw-h-3.5 tw-animate-spin" />
                              ) : (
                                <Upload className="tw-w-3.5 tw-h-3.5" />
                              )}
                              <span>Uploader</span>
                            </button>
                          </>
                        )}
                        <button
                          onClick={handleDownloadPdf}
                          disabled={!pdfBlob}
                          className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-lg tw-text-sm tw-font-medium tw-text-affinicar-primary hover:tw-text-affinicar-dark hover:tw-bg-affinicar-lighter tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                          title="Télécharger le contrat"
                        >
                          {isPdfLoading ? (
                            <Loader2 className="tw-w-3.5 tw-h-3.5 tw-animate-spin" />
                          ) : (
                            <Download className="tw-w-3.5 tw-h-3.5" />
                          )}
                          <span>Télécharger</span>
                        </button>
                      </div>
                    }
                  />
                </div>
              )
            ) : (
              <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-4 tw-shadow-sm tw-text-center tw-text-sm tw-text-gray-400">
                Aucun document PDF
              </div>
            )}

          </div>

        </div>

        {/* Historique */}
        <Can permission="contract.view_audit_logs">
          <GenericAuditLogsCard
            entityType="contract"
            entityId={effectiveId!}
            title="Historique du contrat"
            includeCreationDetails={true}
            shouldGroupCreationLogs={true}
          />
        </Can>

      </div>

      {/* Modale paiement en direct (depuis alerte) */}
      {paymentDirectLink && (
        <DirectPaymentModal
          isOpen={!!paymentDirectLink}
          onClose={() => setPaymentDirectLink(null)}
          paymentLink={paymentDirectLink}
          onPaymentSuccess={handlePaymentSuccess}
          successMessage="Remplissez les informations de paiement du bénéficiaire pour procéder au règlement."
        />
      )}

      {/* Dialog confirmation renvoi lien paiement (depuis alerte) */}
      <SendNotificationConfirmDialog
        isOpen={paymentResendOpen}
        title="Renvoyer le lien de paiement"
        defaultEmail={contract.beneficiary?.email || ''}
        defaultPhone={contract.beneficiary?.phoneNumber || ''}
        isLoading={isResendingPayment}
        onConfirm={handleResendPaymentConfirm}
        onCancel={() => setPaymentResendOpen(false)}
      />

      {/* Modales actions contrat */}
      {contract && (
        <>
          <EditContractInfoModal
            contractId={contract.id}
            beneficiary={contract.beneficiary}
            vehicle={contract.vehicle}
            isOpen={editModalOpen}
            onClose={() => setEditModalOpen(false)}
          />
          <CancelContractModal
            contractId={contract.id}
            contractLabel={contract.label}
            hasSignature={!!contract.signature}
            hasPayment={!!contract.monthlyPaymentId}
            hasInvoice={!!(contract as any).idInvoice}
            servicePrice={contract.servicePrice}
            assistancePrice={contract.assistancePrice ?? null}
            cancelationReason={(contract as any).cancelationReason}
            cancelationDetails={(contract as any).cancelationDetails}
            savs={(contract as any).savs}
            assistanceFormulaId={contract.assistanceFormulaId ?? null}
            assistanceLabel={contract.assistanceLabel ?? null}
            assistanceBenefits={contract.assistanceBenefits ?? null}
            isOpen={cancelModalOpen}
            onClose={() => setCancelModalOpen(false)}
          />
          <RefundContractModal
            contractId={contract.id}
            contractLabel={contract.label}
            isOpen={refundModalOpen}
            onClose={() => setRefundModalOpen(false)}
          />
          <OfferContractModal
            contractId={contract.id}
            contractLabel={contract.label}
            servicePrice={contract.servicePrice}
            assistancePrice={contract.assistancePrice ?? null}
            isOpen={offerModalOpen}
            onClose={() => setOfferModalOpen(false)}
          />
          <RequestCancellationModal
            contractId={contract.id}
            contractLabel={contract.label}
            isOpen={requestCancelModalOpen}
            onClose={() => setRequestCancelModalOpen(false)}
          />
          <SendSavAccessEmailModal
            contractId={contract.id}
            isOpen={savAccessModalOpen}
            onClose={() => setSavAccessModalOpen(false)}
          />
          <ReactivateStripeSubscriptionModal
            contractId={contract.id}
            subscriptionUuid={contract.subscriptionUuid}
            contractLabel={contract.label}
            contractDuration={contract.duration}
            isOpen={reactivateModalOpen}
            onClose={() => setReactivateModalOpen(false)}
          />
        </>
      )}
    </div>
  );
}
